import React, { useEffect, useState, useRef, useCallback } from "react";
import { throttle } from "lodash";
import { CourtWrapperStyles as styles } from "./court.tailwind";
import { MAX_SCALE, MIN_SCALE, SWITCH_SCALE, MAX_ZOOM_SCALE, DEFAULT_W, DEFAULT_H } from "./court.data";
import ZoomControls from "./ZoomControlTest";
import InnerCourt from "./InnerCourt";
import OuterCourt from "./OuterCourt";
import { getCurrentTimestamps } from "../../../../lib/utils/dateAndTimeFormatting";

const MainSVGComponent = React.forwardRef(({ courtOuterSvgId, currentCommentary, showZoneOnMobileScreen, onViewBoxChange, setScale, scale, viewBox, previewGame, showHoveredTooltip, makeSelectable, selectZone, selectedZones, zonesToBeHighlighted, hideZoneName, isLiveGame, teamDetails, purchasedTokens, isDeployTokenPage, hideHover, disableTokenHover }, svgRef) => {
  const containerRef = useRef(null);
  const isPanningRef = useRef(false);
  const viewBoxRef = useRef(viewBox); // Use ref to store the current viewBox
  const animationFrameRef = useRef(null);

  const [startPoint, setStartPoint] = useState({ x: 0, y: 0 });
  const [isSecondSvgOpen, setIsSecondSvgOpen] = useState(false);
  const [mouseHoldDuration, setMouseHoldDuration] = useState(0);
  const [selectedTokens, setSelectedTokens] = useState([]);

  const initialScale = 0.8;
  const initialViewBox = { 
    x: (DEFAULT_W - (DEFAULT_W / initialScale)) / 2, // Perfect Center X
    y: (DEFAULT_H - (DEFAULT_H / initialScale)) / 2, // Perfect Center Y
    w: DEFAULT_W / initialScale, 
    h: DEFAULT_H / initialScale 
  };

  // modified
  // const animateZoom = (startViewBox, targetViewBox, duration) => {
  //   const startTime = performance.now();
  //   // console.log(startViewBox, targetViewBox)
  //   const animate = (currentTime) => {
  //     const elapsedTime = currentTime - startTime;
  //     const progress = Math.min(elapsedTime / duration, 1);

  //     const interpolate = (start, end) => start + (end - start) * progress;
  //     updateViewBox(targetViewBox)

  //     const svgWidth = svgRef.current.clientWidth;
  //     // const potentialScale = svgWidth / targetViewBox.w;

  //     const newViewBox = {
  //       x: interpolate(startViewBox.x, targetViewBox.x),
  //       y: interpolate(startViewBox.y, targetViewBox.y),
  //       w: interpolate(startViewBox.w, targetViewBox.w),
  //       h: interpolate(startViewBox.h, targetViewBox.h),
  //     };

  //     // setViewBox(newViewBox);
  //     updateViewBox(newViewBox)

  //     if (progress < 1) {
  //       requestAnimationFrame(animate);
  //     } else {
  //       const svgWidth = svgRef.current.clientWidth;
  //       const newScale = svgWidth / newViewBox.w;
  //       // console.log(newScale)
  //       // If newScale exceeds max, adjust viewBox to maintain max scale
  //       if (newScale > MAX_ZOOM_SCALE) {
  //         const adjustedWidth = svgWidth / MAX_ZOOM_SCALE;
  //         const adjustedHeight = (adjustedWidth * newViewBox.h) / newViewBox.w;

  //         newViewBox.x;
  //         newViewBox.w = adjustedWidth ;
  //         newViewBox.h = adjustedHeight;

  //         // console.log("modified")
  //         setScale(MAX_ZOOM_SCALE - 1);
  //       } else {
  //         // console.log("Simple")
  //         setScale(newScale - 0.5);
  //       }
  //       onViewBoxChange(newViewBox); // Notify parent component
  //     }
  //   };

  //   requestAnimationFrame(animate);
  // };

  // old 30, dec 2024

  const animateZoom = useCallback((startViewBox, targetViewBox, duration) => {
    const startTime = performance.now();

    const animate = (currentTime) => {
      const elapsedTime = currentTime - startTime;
      const progress = Math.min(elapsedTime / duration, 1);

      const interpolate = (start, end) => start + (end - start) * progress;

      const newViewBox = {
        x: interpolate(startViewBox.x, targetViewBox.x),
        y: interpolate(startViewBox.y, targetViewBox.y),
        w: interpolate(startViewBox.w, targetViewBox.w),
        h: interpolate(startViewBox.h, targetViewBox.h),
      };

      updateViewBox(newViewBox);

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        const svgWidth = svgRef.current?.clientWidth;
        if (svgWidth) {
          const newScale = svgWidth / targetViewBox.w;
          setScale(newScale);
          onViewBoxChange(newViewBox);
        }
      }
    };

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    animationFrameRef.current = requestAnimationFrame(animate);
  }, [setScale, onViewBoxChange]);

  const handleZoneClick = useCallback((e, zoneCat) => {
    // e.stopPropagation();
    if (!svgRef.current) return;

    e?.preventDefault();
    e?.stopPropagation();

    if (makeSelectable) {
      selectZone(zoneCat);
      return;
    }

    if (previewGame || isPanningRef.current || mouseHoldDuration > 200) return;

    const zone = e.target;
    const bbox = zone.getBBox();

    // Get current screen width
    const screenWidth = window.innerWidth;

    // Adjust padding and scale based on screen size
    const padding = screenWidth < 800 ? 15 : 30;
    const scaleAdjustment = screenWidth < 800 ? 0.9 : 0.75; // Increase scale for smaller screens

    // Calculate aspect ratio of the original viewBox
    const originalAspectRatio = DEFAULT_W / DEFAULT_H;

    // Calculate new width and height with adjusted scaling
    let newWidth = bbox.width * scaleAdjustment;
    let newHeight = bbox.height * scaleAdjustment;

    // Adjust the new dimensions to maintain aspect ratio
    if (newWidth / newHeight > originalAspectRatio) {
      newHeight = newWidth / originalAspectRatio;
    } else {
      newWidth = newHeight * originalAspectRatio;
    }

    const newViewBox = {
      x: bbox.x - (newWidth - bbox.width) / 2 - padding,
      y: bbox.y - (newHeight - bbox.height) / 2 - padding,
      w: newWidth + padding * 2,
      h: newHeight + padding * 2,
    };

    // Ensure new viewBox scaling is within limits
    const svgWidth = svgRef.current.clientWidth;
    const newScale = svgWidth / newViewBox.w;
    const clampedScale = Math.min(Math.max(newScale, MIN_SCALE), MAX_ZOOM_SCALE);

    // Adjust the new viewBox with the clamped scale
    const adjustedViewBox = {
      ...newViewBox,
      w: svgWidth / clampedScale,
      h: (svgWidth / clampedScale) * (newViewBox.h / newViewBox.w),
    };

    const currentViewBox = viewBoxRef.current;

    // Adjust animation duration for smaller screens
    const animationDuration = screenWidth < 800 ? 300 : 500;

    // Start the animation
    animateZoom(currentViewBox, adjustedViewBox, animationDuration);

    // optional scrolling animation to ensure that minimap is visible
    // Scroll to the bottom of the window after a short delay to allow for any rendering
    setTimeout(() => {
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: 'smooth' // Optional: smooth scrolling
      });
    }, animationDuration); // Delay for the duration of the animation

  }, [makeSelectable, selectZone, previewGame, animateZoom, isPanningRef, mouseHoldDuration]);

  const updateViewBox = useCallback((newViewBox) => {
    if (!(newViewBox.w > 2100) && svgRef.current) {
      viewBoxRef.current = newViewBox;
      onViewBoxChange(newViewBox);
      svgRef.current.setAttribute(
        "viewBox",
        `${newViewBox.x} ${newViewBox.y} ${newViewBox.w} ${newViewBox.h}`
      );
    }
  }, [onViewBoxChange]);

  const handleWheel = useCallback(
    throttle((e) => {
      e.preventDefault();
      if (!svgRef.current) return;

      const svgSize = { w: svgRef.current.clientWidth, h: svgRef.current.clientHeight };
      const { x, y, w, h } = viewBoxRef.current;

      const zoomFactor = 0.1;
      const zoomDelta = e.deltaY < 0 ? 1 : -1;
      const dw = w * zoomFactor * zoomDelta;
      const dh = h * zoomFactor * zoomDelta;

      const mx = e.offsetX / svgSize.w;
      const my = e.offsetY / svgSize.h;

      const newViewBox = {
        x: x + mx * dw,
        y: y + my * dh,
        w: w - dw,
        h: h - dh,
      };

      // Calculate the new scale and ensure it is within the min and max scale
      const newScale = svgSize.w / newViewBox.w;

      if (newScale >= MIN_SCALE && newScale <= MAX_SCALE) {
        setScale(newScale);
        updateViewBox(newViewBox);
      } else if (zoomDelta < 1) {
        // console.log(" zoom out ");
      }
    }, 16),
    [updateViewBox, setScale]
  );

  const handleMouseDown = (e) => {
    // e.stopPropagation();
    isPanningRef.current = true;
    setStartPoint({ x: e.clientX, y: e.clientY });

    setMouseHoldDuration(getCurrentTimestamps());
  };

  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    isPanningRef.current = true;
    setStartPoint({ x: touch.clientX, y: touch.clientY });
  };

  // const handleMouseMove = useCallback(
  //   throttle((e) => {
  //     if (!isPanningRef.current) return;

  //     // Add a small threshold to reduce unnecessary updates
  //     const threshold = 1; // Adjust this value to find the right balance
  //     const dx = (startPoint.x - e.clientX) / scale;
  //     const dy = (startPoint.y - e.clientY) / scale;

  //     // Only update if movement exceeds the threshold
  //     if (Math.abs(dx) > threshold || Math.abs(dy) > threshold) {
  //       const newViewBox = {
  //         x: viewBoxRef.current.x + dx,
  //         y: viewBoxRef.current.y + dy,
  //         w: viewBoxRef.current.w,
  //         h: viewBoxRef.current.h,
  //       };

  //       if (
  //         (newViewBox.x < -600 || newViewBox.x > 600 ||
  //           newViewBox.y < -300 || newViewBox.y > 300) && !isSecondSvgOpen
  //       ) {
  //         return;
  //       }

  //       updateViewBox(newViewBox);
  //       setStartPoint({ x: e.clientX, y: e.clientY });
  //     }
  //   }, 16),
  //   [startPoint, scale, updateViewBox]
  // );

  // const handleMouseMove = useCallback(
  //   throttle((e) => {
  //     if (!isPanningRef.current) return;

  //     const svgContainer = containerRef.current;
  //     const svgElement = svgRef.current;

  //     if (!svgContainer || !svgElement) return;

  //     // Get the SVG's current bounding rectangle
  //     const svgRect = svgElement.getBoundingClientRect();

  //     // Calculate delta considering the current scale
  //     const dx = ((startPoint.x - e.clientX) / scale) * (svgRect.width / svgElement.clientWidth);
  //     const dy = ((startPoint.y - e.clientY) / scale) * (svgRect.height / svgElement.clientHeight);

  //     const newViewBox = {
  //       x: viewBoxRef.current.x + dx,
  //       y: viewBoxRef.current.y + dy,
  //       w: viewBoxRef.current.w,
  //       h: viewBoxRef.current.h,
  //     };

  //     // Prevent updates beyond boundaries when not in second SVG
  //     if (
  //       (newViewBox.x < -600 || newViewBox.x > 600 ||
  //         newViewBox.y < -300 || newViewBox.y > 300) && !isSecondSvgOpen
  //     ) {
  //       return;
  //     }

  //     updateViewBox(newViewBox);
  //     setStartPoint({ x: e.clientX, y: e.clientY });
  //   }, 16),
  //   [startPoint, scale, updateViewBox, isSecondSvgOpen]
  // );

  // const handleMouseMove = useCallback(
  //   throttle((e) => {
  //     if (!isPanningRef.current) return;

  //     const dx = (startPoint.x - e.clientX) / scale;
  //     const dy = (startPoint.y - e.clientY) / scale;

  //     const newViewBox = {
  //       x: viewBoxRef.current.x + dx,
  //       y: viewBoxRef.current.y + dy,
  //       w: viewBoxRef.current.w,
  //       h: viewBoxRef.current.h,
  //     };

  //     // Prevent updates beyond boundaries when not in second SVG
  //     if (
  //       (newViewBox.x < -600 || newViewBox.x > 600 ||
  //         newViewBox.y < -300 || newViewBox.y > 300) && !isSecondSvgOpen
  //     ) {
  //       return;
  //     }

  //     updateViewBox(newViewBox);
  //     setStartPoint({ x: e.clientX, y: e.clientY });
  //   }, 16),
  //   [startPoint, scale, updateViewBox, isSecondSvgOpen]
  // );

  const handleMouseMove = useCallback(
    throttle((e) => {
      if (!isPanningRef.current) return;

      // Add a movement threshold to minimize jitter
      const movementThreshold = 2; // Adjust as needed
      const dx = (startPoint.x - e.clientX) / scale;
      const dy = (startPoint.y - e.clientY) / scale;

      // Ignore minor movements to reduce jitter
      if (Math.abs(dx) < movementThreshold && Math.abs(dy) < movementThreshold) {
        return;
      }

      const newViewBox = {
        x: viewBoxRef.current.x + dx,
        y: viewBoxRef.current.y + dy,
        w: viewBoxRef.current.w,
        h: viewBoxRef.current.h,
      };

      // Boundary checks
      if (
        (newViewBox.x < -600 || newViewBox.x > 600 ||
          newViewBox.y < -300 || newViewBox.y > 300) && !isSecondSvgOpen
      ) {
        return;
      }

      // Use requestAnimationFrame for smoother updates
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      animationFrameRef.current = requestAnimationFrame(() => {
        updateViewBox(newViewBox);
        setStartPoint({ x: e.clientX, y: e.clientY });
      });
    }, 16), // 16ms for ~60fps
    [startPoint, scale, updateViewBox, isSecondSvgOpen]
  );

  const handleTouchMove = (e) => {
    if (e.touches.length === 2) {
      // Pinch-to-zoom
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];

      const currentDistance = Math.sqrt(
        Math.pow(touch2.clientX - touch1.clientX, 2) +
        Math.pow(touch2.clientY - touch1.clientY, 2)
      );

      if (!viewBoxRef.current.pinchDistance) {
        viewBoxRef.current.pinchDistance = currentDistance;
        return;
      }

      const zoomDelta = (currentDistance - viewBoxRef.current.pinchDistance) / 200;

      const svgSize = { w: svgRef.current.clientWidth, h: svgRef.current.clientHeight };
      const { x, y, w, h } = viewBoxRef.current;

      const newViewBox = {
        x: x + (w * zoomDelta) / 2,
        y: y + (h * zoomDelta) / 2,
        w: w - w * zoomDelta,
        h: h - h * zoomDelta,
      };

      setScale(svgSize.w / newViewBox.w);
      updateViewBox(newViewBox);
      viewBoxRef.current.pinchDistance = currentDistance;
    } else if (e.touches.length === 1 && isPanningRef.current) {
      // Pan
      const touch = e.touches[0];
      const dx = (startPoint.x - touch.clientX) / scale;
      const dy = (startPoint.y - touch.clientY) / scale;

      const newViewBox = {
        x: viewBoxRef.current.x + dx,
        y: viewBoxRef.current.y + dy,
        w: viewBoxRef.current.w,
        h: viewBoxRef.current.h,
      };

      updateViewBox(newViewBox);
      setStartPoint({ x: touch.clientX, y: touch.clientY });
    }
  };

  const handleMouseUp = () => {
    isPanningRef.current = false;

    setMouseHoldDuration((prev) => (getCurrentTimestamps() - prev));
  };

  const handleTouchEnd = () => {
    isPanningRef.current = false;
    viewBoxRef.current.pinchDistance = null; // Reset pinch distance
  };

  const handleReset = () => {
    updateViewBox(initialViewBox);
    setScale(initialScale);
    onViewBoxChange(initialViewBox);
  };

  const getZoomInFactor = (isSecondSvgOpen, width) => {
    if (!isSecondSvgOpen) {
      switch (true) {
        case width > 500 && width <= 768:
          return 0.68;
        case width > 768 && width <= 820:
          return 0.65;
        case width > 820 && width <= 950:
          return 0.62;
        case width > 950 && width <= 1024:
          return 0.56;
        case width > 1024:
          return 0.4;
        default:
          return 0.1;
      }
    } else {
      return 0.1;
    }
  };

  const handleZoomIn = () => {
    const svgSize = {
      w: svgRef.current.clientWidth,
      h: svgRef.current.clientHeight,
    };

    if ((scale + 0.2) < MAX_SCALE) {

      // Get the current viewport dimensions and position
      const { x, y, w, h } = viewBoxRef.current;

      const e = {
        offsetX: containerRef.current.clientWidth / 2,
        offsetY: containerRef.current.clientHeight / 2,
      }

      // const zoomFactor = getZoomInFactor(isSecondSvgOpen, window.innerWidth)
      const zoomFactor = getZoomInFactor(isSecondSvgOpen, window.innerWidth)

      const zoomDelta = 1;

      const mx = e.offsetX;
      const my = e.offsetY;

      const dw = w * zoomFactor * zoomDelta;
      const dh = h * zoomFactor * zoomDelta;

      const dx = (dw * mx) / svgSize.w;
      const dy = (dh * my) / svgSize.h;

      let newViewBox = {
        x: x + dx,
        y: y + dy,
        w: w - dw,
        h: h - dh,
      };

      // Instead of immediately setting the view box, animate the transition
      if (!isSecondSvgOpen) {
        const potentialScale = svgSize.w / newViewBox.w;

        // If scale would be too high, adjust the viewBox to maintain desired scale
        if (potentialScale > 1 && potentialScale > 1.2) {
          // console.log("fas gya");

          // Target scale we want (between 1 and 1.2)
          const targetScale = 1.1; // You can adjust this value between 1.0 and 1.2

          // Calculate new width and height based on target scale
          const adjustedWidth = svgSize.w / targetScale;
          const adjustedHeight = (adjustedWidth / newViewBox.w) * newViewBox.h;

          // Keep the center point the same while adjusting the size
          const centerX = newViewBox.x + newViewBox.w / 2;
          const centerY = newViewBox.y + newViewBox.h / 2;

          // Update viewBox with new dimensions while maintaining center point
          newViewBox = {
            x: centerX - adjustedWidth / 2,
            y: centerY - adjustedHeight / 2,
            w: adjustedWidth,
            h: adjustedHeight,
          };
        }
        // console.log(newViewBox);

        animateZoom(viewBoxRef.current, newViewBox, 300); // Adjust the duration as needed
      } else {
        const newScale = svgSize.w / newViewBox.w;
        setScale(newScale);
        updateViewBox(newViewBox);
        onViewBoxChange(newViewBox); // Ensure minimap gets updated
      }
    }
  };

  const getZoomOutFactor = (width) => {
    // console.log("width: " + width)
    // console.log("isSecondSvgOpen: " + isSecondSvgOpen)
    // if (isSecondSvgOpen) {
    //   switch (true) {
    //     case width > 500 && width <= 768:
    //       return 0.7;
    //     case width > 768 && width <= 820:
    //       return 0.75;
    //     case width > 820 && width <= 950:
    //       return 0.78;
    //     case width > 950 && width <= 1024:
    //       return 0.82;
    //     case width > 1024:
    //       return 0.85;
    //     default:
    //       return 1; // Default zoom out factor
    //   }
    // } else {
    //   return 0.1; // Default zoom out factor for second SVG
    // }
    switch (true) {
      case width > 500 && width <= 768:
        return 0.357;
      case width > 768 && width <= 820:
        return 0.32;
      case width > 820 && width <= 950:
        return 0.32;
      case width > 950 && width <= 1024:
        return 0.31;
      case width > 1024 && width <= 1440:
        return 0.24;
      default:
        return 0.24; // Default zoom out factor
    }
  };
  // console.log("isSecondSvgOpen**: " + isSecondSvgOpen)

  const zoomOutLogic = () => {
    const svgSize = {
      w: svgRef.current.clientWidth,
      h: svgRef.current.clientHeight,
    };

    // Get the current viewport dimensions and position
    const { x, y, w, h } = viewBoxRef.current;

    const e = {
      offsetX: containerRef.current.clientWidth / 2,
      offsetY: containerRef.current.clientHeight / 2,
    }
    // console.log("isSecondSvgOpen*: " + isSecondSvgOpen)
    const zoomFactor = getZoomOutFactor(isSecondSvgOpen, window.innerWidth);
    // console.log("zoomFactor: " + zoomFactor)
    const zoomDelta = -1;

    const mx = e.offsetX;
    const my = e.offsetY;

    const dw = w * zoomFactor * zoomDelta;
    const dh = h * zoomFactor * zoomDelta;

    const dx = (dw * mx) / svgSize.w;
    const dy = (dh * my) / svgSize.h;

    const newViewBox = {
      x: x + dx,
      y: y + dy,
      w: w - dw,
      h: h - dh,
    };
    // console.log("newViewbox w:", newViewBox.w)
    // it was 2100 before
    if (!(newViewBox.w > 2100)) {
      const newScale = svgSize.w / newViewBox.w;
      // console.log("newScale", newScale)
      // console.log(newScale > MAX_SCALE ? newScale - 0.5 : newScale);

      // Instead of immediately setting the view box, animate the transition
      // animateZoom(viewBoxRef.current, newViewBox, 300); // Adjust the duration as needed

      if ((newScale - 0.2 < MIN_SCALE) && window.innerWidth < 850) {
        // console.log("work on it ", newScale, newScale - 0.2)
        handleReset();
        return;
      }
      setScale(newScale > MAX_SCALE ? newScale - 0.5 : newScale);
      updateViewBox(newViewBox);
      onViewBoxChange(newViewBox); // Ensure minimap gets updated
    }
    else {
      // console.log("you need to do something");
      handleReset();
    }
  }

  const handleZoomOut = () => {
    // if (window.innerWidth < 800) {
    //   zoomOutLogic()
    // }
    // else if (scale - 0.2 > MIN_SCALE) {
    //   console.log("in the else if");
    //   zoomOutLogic();
    // }
    // else {
    //   console.warn("in the else");
    // }
    if ((scale - 0.2) > MIN_SCALE) {
      // console.log("in zoomOut", scale, scale - 0.2)
      zoomOutLogic();
    } else {
      handleReset();
    }
  };

  useEffect(() => {
    viewBoxRef.current = viewBox; // Sync ref with initial state
    if (svgRef.current) {
      svgRef.current.setAttribute(
        "viewBox",
        `${viewBox.x} ${viewBox.y} ${viewBox.w} ${viewBox.h}`
      );
    }
  }, []);

  useEffect(() => {
    // console.log(`scale change ${scale} ${scale > SWITCH_SCALE}`)
    setIsSecondSvgOpen(scale > SWITCH_SCALE);
  }, [scale]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !svgRef.current || previewGame) return; // Ensure both container and SVG refs are set

    container.addEventListener("wheel", handleWheel, { passive: false });
    container.addEventListener("mousedown", handleMouseDown);
    container.addEventListener("mousemove", handleMouseMove);
    container.addEventListener("mouseup", handleMouseUp);
    container.addEventListener("mouseleave", handleMouseUp);

    // Add touch events
    container.addEventListener("touchstart", handleTouchStart);
    container.addEventListener("touchmove", handleTouchMove, { passive: false });
    container.addEventListener("touchend", handleTouchEnd);

    updateViewBox(viewBox);

    return () => {
      container.removeEventListener("wheel", handleWheel);
      container.removeEventListener("mousedown", handleMouseDown);
      container.removeEventListener("mousemove", handleMouseMove);
      container.removeEventListener("mouseup", handleMouseUp);
      container.removeEventListener("mouseleave", handleMouseUp);

      // Remove touch events
      container.removeEventListener("touchstart", handleTouchStart);
      container.removeEventListener("touchmove", handleTouchMove);
      container.removeEventListener("touchend", handleTouchEnd);
    };
  }, [handleMouseDown, handleMouseMove, handleMouseUp, previewGame]);

  useEffect(() => {
    const svg = document.getElementById(courtOuterSvgId);
    if (!svg) return;

    const handleClick = (e) => {
      const zoneHref = e.currentTarget.getAttribute('href');
      if (zoneHref) {
        const zoneCat = zoneHref.split('zone')[1];
        handleZoneClick(e, zoneCat);
      }
    };

    const aTags = svg.querySelectorAll("a");
    aTags.forEach((a) => {
      a.addEventListener('click', handleClick);
    });

    return () => {
      aTags.forEach((a) => {
        a.removeEventListener('click', handleClick);
      });
    };
  }, [handleZoneClick, courtOuterSvgId]);

  // Cleanup animation frame on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  // console.log(scale)

  return (
    <>
      {!previewGame && <ZoomControls
        handleReset={handleReset}
        handleZoomIn={handleZoomIn}
        handleZoomOut={handleZoomOut}
        scale={scale}
        MAX_SCALE={MAX_SCALE}
        isZoomInDisabled={scale >= (MAX_SCALE - 0.5)}
        isZoomOutDisabled={scale - 0.2 < MIN_SCALE || scale === 1}
      />}

      <div
        className={styles.mainSVgContainer}
        ref={containerRef}
      >
        {(isSecondSvgOpen && !previewGame) ? (
          <InnerCourt
            ref={svgRef}
            isPanningRef={isPanningRef}
            viewBox={viewBox}
            selectedTokens={selectedTokens}
            setSelectedTokens={setSelectedTokens}
          />
        ) : (
          <OuterCourt
            ref={svgRef}
            viewBox={viewBox}
            makeSelectable={makeSelectable}
            zonesToBeHighlighted={zonesToBeHighlighted}
            selectedZones={selectedZones}
            showHoveredTooltip={showHoveredTooltip}
            hideZoneName={hideZoneName}
            isLiveGame={isLiveGame}
            teamDetails={teamDetails}
            purchasedTokens={purchasedTokens}
            isDeployTokenPage={isDeployTokenPage}
            hideHover={hideHover}
            disableTokenHover={disableTokenHover}
            courtOuterSvgId={courtOuterSvgId}
            showZoneOnMobileScreen={showZoneOnMobileScreen}
            currentCommentary={currentCommentary}
          />
        )}

      </div>
    </>
  );
}
);

MainSVGComponent.displayName = "MainSVGComponent";

export default MainSVGComponent;
